import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { api, ApiError } from "@/lib/api";
import { useSession, type MeResponse } from "@/lib/session";

export function SignIn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useSession();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const me = await api.post<MeResponse>("/platform/auth/login", {
        email,
        password,
      });

      // Refresh the cached /me so guards see the new session immediately.
      await refresh();

      if (!me.has_platform_access) {
        navigate("/no-access", { replace: true });
        return;
      }

      const next = params.get("next");
      navigate(next && next.startsWith("/") ? next : "/", { replace: true });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : "Something went wrong. Try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="w-full max-w-[420px] anim-fade-up">
      <div
        className="rounded-[var(--radius-modal)] p-8 shadow-xl-eon"
        style={{
          background: "rgba(11, 13, 18, 0.55)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(36px) saturate(180%)",
          WebkitBackdropFilter: "blur(36px) saturate(180%)",
        }}
      >
        <div className="mb-6">
          <p className="font-mono text-[11px] uppercase tracking-[0.06em] text-[var(--color-slate-200)]">
            Platform
          </p>
          <h1 className="mt-1 text-heading text-[var(--color-snow)] font-semibold">
            Sign in
          </h1>
          <p className="mt-2 text-[14px] text-[var(--color-slate-200)]">
            Operate autonomous health agents for your members.
          </p>
        </div>

        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          <label className="block">
            <span className="block text-[12px] font-medium text-[var(--color-slate-200)] mb-1.5">
              Email
            </span>
            <Input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourbrand.com"
            />
          </label>

          <label className="block">
            <span className="block text-[12px] font-medium text-[var(--color-slate-200)] mb-1.5">
              Password
            </span>
            <Input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>

          {error && (
            <p className="text-[13px] text-[var(--color-ember)]">{error}</p>
          )}

          <Button
            type="submit"
            disabled={submitting || !email || !password}
            className="w-full"
            size="lg"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <div className="mt-5 flex items-center justify-between text-[13px]">
          <Link
            to="/forgot"
            className="text-[var(--color-slate-200)] hover:text-[var(--color-snow)] transition-colors"
          >
            Forgot password?
          </Link>
          <Link
            to="/signup"
            className="text-[var(--color-slate-300)] hover:text-[var(--color-snow)] transition-colors"
          >
            No account? →
          </Link>
        </div>
      </div>
    </div>
  );
}
